/**
 * ReviewThreadPage — full-screen review-thread viewer for the standalone
 * Review Thread pop-out window.
 *
 * Rendered by the `?rt=1` branch in `main.tsx` after the API client has been
 * configured for the operator. This page fills the entire OS window
 * (100vw × 100vh) and renders the shared `ReviewThreadView` so the operator
 * sees the original review, the developer reply, and any customer follow-up in
 * a single chronological chain — the same pop-out workflow used for support
 * email customer history, keyed by interaction id instead of email.
 *
 * Layout:
 *   ┌──────────────────────────────────┐
 *   │  Header  (review thread title)   ~52px flexShrink:0
 *   ├──────────────────────────────────┤
 *   │                                  │
 *   │  ReviewThreadView                flex:1 (manages its own scroll)
 *   │                                  │
 *   └──────────────────────────────────┘
 */

import type { CSSProperties, ReactNode } from 'react'
import { MessageSquare } from 'lucide-react'
import { ReviewThreadView } from '@/features/reviews/components/ReviewThreadView'

export interface ReviewThreadPageProps {
  interactionId: string
  reviewerName: string | null
  appName: string | null
  /**
   * Fill the parent element instead of the viewport (Task 4094).
   * Same contract as `CustomerHistoryPageProps.embedded`: the desktop pop-out
   * owns an OS window and wants viewport units; the browser panel hosts this
   * page inside a box and wants `100%`.
   */
  embedded?: boolean
}

export function ReviewThreadPage({
  interactionId,
  reviewerName,
  appName,
  embedded = false,
}: ReviewThreadPageProps): ReactNode {
  const displayName = reviewerName ?? 'Review thread'
  const subtitle    = appName ?? null

  return (
    <div style={embedded ? { ...styles.root, ...styles.rootEmbedded } : styles.root}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerIcon}>
          <MessageSquare style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }} />
        </div>
        <div style={styles.headerText}>
          <span style={styles.headerName}>{displayName}</span>
          {subtitle && <span style={styles.headerSubtitle}>{subtitle}</span>}
        </div>
      </div>

      {/* Body — ReviewThreadView owns its own internal scroll */}
      <div style={styles.body}>
        <div style={styles.scroll}>
          <ReviewThreadView interactionId={interactionId} />
        </div>
      </div>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
  },
  /** Overrides for the in-page panel host — see the `embedded` prop. */
  rootEmbedded: {
    height: '100%',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 20px',
    borderBottom: '1px solid var(--border)',
    flexShrink: 0,
    background: 'var(--bg-secondary)',
  },
  headerIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    background: 'var(--bg-tertiary)',
    flexShrink: 0,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    minWidth: 0,
  },
  headerName: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerSubtitle: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
}

export default ReviewThreadPage
